import { useAppDispatch, useAppSelector } from "@/lib/store";
import { useEffect, useCallback, useRef } from "react";
import {
  closeSheet,
  fetchAppointmentFilterOptions,
  fetchAppointments,
  openSheet,
  saveAppointment,
  setAssignee,
  setDateFrom,
  setDateTo,
  setPage,
  setPageSize,
  setSearch,
  setServiceParent,
  setSortKey,
  setStatus,
  setVendor,
} from "./appointments.slice";
import { CreateAppointmentPayload } from "@/app/pmc/appointments/appointments.entity.ts";
import { useDebounce } from "@/hooks/useDebounce";

export const useAppointments = () => {
  const dispatch = useAppDispatch();
  const state = useAppSelector((s) => s.appointments);

  // Debounce search input by 400ms to prevent excessive API calls
  // Keep Redux search updating immediately for responsive input display
  // Pass debounced value directly to fetch - no sync needed, prevents blocking input
  const debouncedSearch = useDebounce(state.filters.search, 400);

  useEffect(() => {
    // Pass debounced search directly to fetch - this prevents requests on every keystroke
    // while keeping the input responsive (no sync back to Redux while typing)
    dispatch(fetchAppointments(debouncedSearch));
  }, [
    dispatch,
    state.filters.status,
    state.filters.serviceParent,
    state.filters.vendor,
    state.filters.assignee,
    debouncedSearch, // Use debounced search - fetch only triggers after user stops typing
    state.filters.dateFrom,
    state.filters.dateTo,
    state.filters.sortKey,
    state.filters.sortOrder,
    state.pagination.page,
    state.pagination.pageSize,
  ]);

  useEffect(() => {
    dispatch(fetchAppointmentFilterOptions());
  }, [dispatch]);

  return {
    list: state.list,
    total: state.total,
    loading: state.loading,
    options: state.options,

    filters: {
      ...state.filters,
      setStatus: useCallback((v: string) => dispatch(setStatus(v)), [dispatch]),
      setServiceParent: useCallback((v: string) => dispatch(setServiceParent(v)), [dispatch]),
      setVendor: useCallback((v: string) => dispatch(setVendor(v)), [dispatch]),
      setAssignee: useCallback((v: string) => dispatch(setAssignee(v)), [dispatch]),
      setSearch: useCallback((v: string) => dispatch(setSearch(v)), [dispatch]),
      setDateFrom: useCallback((v: string) => dispatch(setDateFrom(v)), [dispatch]),
      setDateTo: useCallback((v: string) => dispatch(setDateTo(v)), [dispatch]),
      setSortKey: useCallback((k: string) => dispatch(setSortKey(k)), [dispatch]),
    },

    pagination: {
      ...state.pagination,
      setPage: (p: number) => dispatch(setPage(p)),
      setPageSize: (ps: number) => dispatch(setPageSize(ps)),
    },

    // sheet
    sheetOpen: state.sheetOpen,
    openSheet: () => dispatch(openSheet()),
    closeSheet: () => dispatch(closeSheet()),

    create: (dto: CreateAppointmentPayload) => dispatch(saveAppointment(dto)).unwrap(),

    lastCreated: state.lastCreated,

    refetch: () => dispatch(fetchAppointments(state.filters.search)),

    dispatch,
  };
};
